/**
 * Module dependencies.
 */

import { ErrorMessage, FormGroup } from 'src/components/core/forms/styles';
import { HTMLProps, ReactNode, forwardRef } from 'react';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import checkboxCheckedSvg from 'src/assets/svgs/checkbox.svg';
import styled from 'styled-components';

/**
 * Export `CheckboxProps` type.
 */

export type CheckboxProps = Omit<HTMLProps<HTMLInputElement>, 'type' | 'as' | 'label'> & {
  className?: string;
  disabled?: boolean;
  error?: string;
  label: ReactNode;
};

/**
 * `CheckmarkWrapper` styled component.
 */

const CheckmarkWrapper = styled.span`
  border: 2px solid var(--checkbox-outer-border-color);
  border-radius: 4px;
  grid-area: checkbox;
  height: var(--checkbox-size);
  position: relative;
  top: 3px;
  transition: border-color var(--transition-fast);
  width: var(--checkbox-size);
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  color: var(--color-cyan400);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity var(--transition-fast);
`;

/**
 * `Label` styled component.
 */

const Label = styled.label<{
  disabled?: boolean;
  hasError?: boolean;
  hasLabel?: boolean;
}>`
  --checkbox-icon-color: var(--color-slate500);
  --checkbox-outer-border-color: var(--color-slate500);
  --checkbox-size: 18px;

  align-items: flex-start;
  color: var(--checkbox-label-color);
  cursor: pointer;
  display: grid;
  grid-column-gap: var(--space-xxs);
  grid-template-areas: 'checkbox label';
  grid-template-columns: var(--checkbox-size) 1fr;
  padding: 8px 0;
  position: relative;
  width: 100%;
  width: max-content;

  ${Icon} {
    opacity: 0;
  }

  input:checked ~ ${CheckmarkWrapper} {
    --checkbox-inner-border-color: tranparent;

    ${Icon} {
      opacity: 1;
    }
  }

  :focus,
  :focus-visible,
  :hover {
    --checkbox-outer-border-color: var(--color-cyan400);
  }

  :disabled {
    --checkbox-outer-border-color: var(--color-slate400);
    --checkbox-icon-color: var(--color-slate500);

    color: var(--color-slate500);
    pointer-events: none;
  }

  &[data-error='true'] {
    --checkbox-outer-border-color: var(--color-error) !important;
  }
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  cursor: pointer;
  grid-area: checkbox;
  height: var(--checkbox-size);
  opacity: 0;
  width: var(--checkbox-size);
  z-index: 1;
`;

/**
 * `LabelText` styled component.
 */

const LabelText = styled(Text)`
  align-self: center;
  color: var(--color-text);
  grid-area: label;
  transition: var(--transition-default);
  transition-property: color, opacity;
  word-break: break-word;
`;

/**
 * Export `Checkbox` component.
 */

export const Checkbox = forwardRef(
  ({ className, disabled, error, id, label, name, ...props }: CheckboxProps, ref: any) => {
    return (
      <FormGroup className={className} data-cursor={'none'}>
        <Label data-error={!!error} disabled={disabled}>
          <Input disabled={disabled} id={id ?? name} name={name} ref={ref} type={'checkbox'} {...props} />

          <LabelText>{label}</LabelText>

          <CheckmarkWrapper>
            <Icon icon={checkboxCheckedSvg} size={'18px'} />
          </CheckmarkWrapper>
        </Label>

        {!!error && <ErrorMessage disabled={disabled}>{error}</ErrorMessage>}
      </FormGroup>
    );
  }
);

/**
 * `Checkbox` display name.
 */

Checkbox.displayName = 'Checkbox';
